"use client";

import { useState, useEffect } from "react";
import api from "@/lib/api";
import WithdrawalModal from "./WithdrawalModal";

interface UserProfile {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  gender: string | null;
  ageGroup: string | null;
  yogaExperience: string | null;
  healthHistory: string[];
  goals: string[];
  preferredTimes: string[];
}

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Chip component for single selection
function Chip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium transition ${
        selected
          ? "bg-blue-500 text-text-inverse"
          : "bg-surface-subtle text-text-muted hover:bg-surface-subtle"
      }`}
    >
      {label}
    </button>
  );
}

// Multi-select chip component
function MultiChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm font-medium transition border ${
        selected
          ? "bg-blue-50 text-blue-600 border-blue-500"
          : "bg-surface text-text-muted border-border-strong hover:border-border-strong"
      }`}
    >
      {selected && <span className="mr-1">✓</span>}
      {label}
    </button>
  );
}

export default function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [isWithdrawalModalOpen, setIsWithdrawalModalOpen] = useState(false);

  // Original profile for comparison
  const [originalProfile, setOriginalProfile] = useState<UserProfile | null>(
    null,
  );

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [yogaExperience, setYogaExperience] = useState("");
  const [healthHistory, setHealthHistory] = useState<string[]>([]);
  const [goals, setGoals] = useState<string[]>([]);
  const [preferredTimes, setPreferredTimes] = useState<string[]>([]);

  // Check if there are changes
  const hasChanges =
    originalProfile &&
    (name !== (originalProfile.name || "") ||
      phone !== (originalProfile.phone || "") ||
      gender !== (originalProfile.gender || "") ||
      ageGroup !== (originalProfile.ageGroup || "") ||
      yogaExperience !== (originalProfile.yogaExperience || "") ||
      JSON.stringify(healthHistory.sort()) !==
        JSON.stringify((originalProfile.healthHistory || []).sort()) ||
      JSON.stringify(goals.sort()) !==
        JSON.stringify((originalProfile.goals || []).sort()) ||
      JSON.stringify(preferredTimes.sort()) !==
        JSON.stringify((originalProfile.preferredTimes || []).sort()));

  useEffect(() => {
    if (isOpen) {
      fetchProfile();
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const fetchProfile = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/user/profile");
      const profile = response.data.user;
      setOriginalProfile(profile);

      // Set form values
      setName(profile.name || "");
      setEmail(profile.email || "");
      setPhone(profile.phone || "");
      setGender(profile.gender || "");
      setAgeGroup(profile.ageGroup || "");
      setYogaExperience(profile.yogaExperience || "");
      setHealthHistory(profile.healthHistory || []);
      setGoals(profile.goals || []);
      setPreferredTimes(profile.preferredTimes || []);
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      setError("프로필을 불러올 수 없습니다.");
    } finally {
      setLoading(false);
    }
  };

  // Format phone number
  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7)
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  // Toggle multi-select
  const toggleSelection = (
    value: string,
    current: string[],
    setter: (v: string[]) => void,
  ) => {
    if (current.includes(value)) {
      setter(current.filter((v) => v !== value));
    } else {
      setter([...current, value]);
    }
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);

    try {
      await api.put("/api/user/profile", {
        name: name.trim(),
        phone: phone.trim(),
        gender: gender || null,
        ageGroup: ageGroup || null,
        yogaExperience: yogaExperience || null,
        healthHistory,
        goals,
        preferredTimes,
      });

      // Update original profile to reflect saved state
      setOriginalProfile({
        ...originalProfile!,
        name,
        phone,
        gender,
        ageGroup,
        yogaExperience,
        healthHistory,
        goals,
        preferredTimes,
      });

      onClose();
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || "저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Reset to original values if closing without saving
    if (originalProfile) {
      setName(originalProfile.name || "");
      setPhone(originalProfile.phone || "");
      setGender(originalProfile.gender || "");
      setAgeGroup(originalProfile.ageGroup || "");
      setYogaExperience(originalProfile.yogaExperience || "");
      setHealthHistory(originalProfile.healthHistory || []);
      setGoals(originalProfile.goals || []);
      setPreferredTimes(originalProfile.preferredTimes || []);
    }
    onClose();
  };

  if (!isOpen) return null;

  // If withdrawal modal is open, only render the withdrawal modal
  if (isWithdrawalModalOpen) {
    return (
      <WithdrawalModal
        isOpen={isWithdrawalModalOpen}
        onClose={() => setIsWithdrawalModalOpen(false)}
        userEmail={email}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-text/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-surface rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-text">내 정보</h2>
          <button
            onClick={handleClose}
            className="text-text-subtle hover:text-text-muted transition"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8 text-text-muted">로딩 중...</div>
          ) : error && !originalProfile ? (
            <div className="text-center py-8 text-red-500">{error}</div>
          ) : (
            <div className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  이름
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-border-strong rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-text"
                  placeholder="이름을 입력하세요"
                />
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  이메일
                </label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="w-full px-3 py-2 border border-border rounded-md bg-surface-muted text-text-muted cursor-not-allowed"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  휴대폰 번호
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  className="w-full px-3 py-2 border border-border-strong rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-text"
                  placeholder="010-1234-5678"
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  성별
                </label>
                <div className="flex gap-3">
                  <Chip
                    label="남성"
                    selected={gender === "male"}
                    onClick={() => setGender("male")}
                  />
                  <Chip
                    label="여성"
                    selected={gender === "female"}
                    onClick={() => setGender("female")}
                  />
                </div>
              </div>

              {/* Age Group */}
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  연령대
                </label>
                <select
                  value={ageGroup}
                  onChange={(e) => setAgeGroup(e.target.value)}
                  className="w-full px-3 py-2 border border-border-strong rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-text bg-surface"
                >
                  <option value="">선택해주세요</option>
                  <option value="10s">10대 이하</option>
                  <option value="20s">20대</option>
                  <option value="30s">30대</option>
                  <option value="40s">40대</option>
                  <option value="50s">50대</option>
                  <option value="60s+">60대 이상</option>
                </select>
              </div>

              {/* Yoga Experience */}
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  요가 경험
                </label>
                <div className="flex gap-3">
                  <Chip
                    label="초급"
                    selected={yogaExperience === "beginner"}
                    onClick={() => setYogaExperience("beginner")}
                  />
                  <Chip
                    label="중급"
                    selected={yogaExperience === "intermediate"}
                    onClick={() => setYogaExperience("intermediate")}
                  />
                  <Chip
                    label="고급"
                    selected={yogaExperience === "advanced"}
                    onClick={() => setYogaExperience("advanced")}
                  />
                </div>
              </div>

              {/* Health History */}
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  건강 이력{" "}
                  <span className="text-text-subtle font-normal">
                    (복수 선택)
                  </span>
                </label>
                <div className="flex flex-wrap gap-2">
                  <MultiChip
                    label="허리/목 통증"
                    selected={healthHistory.includes("back_neck_pain")}
                    onClick={() =>
                      toggleSelection(
                        "back_neck_pain",
                        healthHistory,
                        setHealthHistory,
                      )
                    }
                  />
                  <MultiChip
                    label="관절 통증"
                    selected={healthHistory.includes("joint_pain")}
                    onClick={() =>
                      toggleSelection(
                        "joint_pain",
                        healthHistory,
                        setHealthHistory,
                      )
                    }
                  />
                  <MultiChip
                    label="최근 수술"
                    selected={healthHistory.includes("recent_surgery")}
                    onClick={() =>
                      toggleSelection(
                        "recent_surgery",
                        healthHistory,
                        setHealthHistory,
                      )
                    }
                  />
                  <MultiChip
                    label="임신"
                    selected={healthHistory.includes("pregnancy")}
                    onClick={() =>
                      toggleSelection(
                        "pregnancy",
                        healthHistory,
                        setHealthHistory,
                      )
                    }
                  />
                </div>
              </div>

              {/* Goals */}
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  목표{" "}
                  <span className="text-text-subtle font-normal">
                    (복수 선택)
                  </span>
                </label>
                <div className="flex flex-wrap gap-2">
                  <MultiChip
                    label="체중 감량"
                    selected={goals.includes("weight_loss")}
                    onClick={() =>
                      toggleSelection("weight_loss", goals, setGoals)
                    }
                  />
                  <MultiChip
                    label="유연성 향상"
                    selected={goals.includes("flexibility")}
                    onClick={() =>
                      toggleSelection("flexibility", goals, setGoals)
                    }
                  />
                  <MultiChip
                    label="스트레스 해소"
                    selected={goals.includes("stress_relief")}
                    onClick={() =>
                      toggleSelection("stress_relief", goals, setGoals)
                    }
                  />
                  <MultiChip
                    label="근력 강화"
                    selected={goals.includes("strength")}
                    onClick={() => toggleSelection("strength", goals, setGoals)}
                  />
                  <MultiChip
                    label="통증 완화"
                    selected={goals.includes("pain_relief")}
                    onClick={() =>
                      toggleSelection("pain_relief", goals, setGoals)
                    }
                  />
                  <MultiChip
                    label="명상"
                    selected={goals.includes("meditation")}
                    onClick={() =>
                      toggleSelection("meditation", goals, setGoals)
                    }
                  />
                </div>
              </div>

              {/* Preferred Times */}
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  선호 시간대{" "}
                  <span className="text-text-subtle font-normal">
                    (복수 선택)
                  </span>
                </label>
                <div className="flex flex-wrap gap-2">
                  <MultiChip
                    label="아침 (6-9시)"
                    selected={preferredTimes.includes("morning")}
                    onClick={() =>
                      toggleSelection(
                        "morning",
                        preferredTimes,
                        setPreferredTimes,
                      )
                    }
                  />
                  <MultiChip
                    label="오전 (9-12시)"
                    selected={preferredTimes.includes("late_morning")}
                    onClick={() =>
                      toggleSelection(
                        "late_morning",
                        preferredTimes,
                        setPreferredTimes,
                      )
                    }
                  />
                  <MultiChip
                    label="오후 (12-18시)"
                    selected={preferredTimes.includes("afternoon")}
                    onClick={() =>
                      toggleSelection(
                        "afternoon",
                        preferredTimes,
                        setPreferredTimes,
                      )
                    }
                  />
                  <MultiChip
                    label="저녁 (18시 이후)"
                    selected={preferredTimes.includes("evening")}
                    onClick={() =>
                      toggleSelection(
                        "evening",
                        preferredTimes,
                        setPreferredTimes,
                      )
                    }
                  />
                </div>
              </div>

              {error && (
                <div className="text-red-500 text-sm text-center">{error}</div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && originalProfile && (
          <div className="p-4 border-t space-y-2">
            {hasChanges ? (
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full py-2 px-4 bg-blue-500 text-text-inverse rounded-md hover:bg-blue-600 transition disabled:bg-surface-subtle disabled:cursor-not-allowed"
              >
                {saving ? "저장 중..." : "저장"}
              </button>
            ) : (
              <>
                <button
                  onClick={() => setIsWithdrawalModalOpen(true)}
                  className="w-full py-2 px-4 bg-surface-subtle text-red-500 rounded-md hover:bg-surface-subtle transition"
                >
                  회원 탈퇴
                </button>
                <button
                  onClick={handleClose}
                  className="w-full py-2 px-4 bg-surface-subtle text-text-muted rounded-md hover:bg-surface-subtle transition"
                >
                  닫기
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
